import React, { useCallback, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import GroupModal from '../../../../../components/Modals/GroupModal.tsx';
import HotLeadCriteriaModal from '../../../../../components/Leads/HotLeadCriteriaModal.tsx';
import { Button, FormConfirmDialog, FormSection } from '../../../../../components/ui';
import useFieldDragReorder from '../../hooks/useFieldDragReorder';
import { fieldMatchesTypeFilter } from '../../utils/fieldTypeAdapter';
import { isStatusField } from '../../utils/formStudioFormUtils';
import { applyParentOptionChangesToFields } from '../../utils/statusOptionSubstatusUtils';
import {
  getLinkedSubStatusEntries,
  updateStatusFieldAndLinks,
} from '../../utils/statusFieldHierarchyUtils';
import FormFieldsToolbar from './FormFieldsToolbar';
import FormFieldCard from './FormFieldCard';
import FormFieldsTableView from './FormFieldsTableView';
import FormFieldDrawer from './FormFieldDrawer';
import { spacing } from '../../../../../theme/tokens/spacing.js';

function filterFields(fields, search, typeFilter) {
  const query = String(search || '').trim().toLowerCase();
  return (fields || []).filter((field) => {
    const matchesType = fieldMatchesTypeFilter(field, typeFilter);
    const haystack = `${field.key_name || ''} ${field.alias_name || ''}`.toLowerCase();
    const matchesSearch = !query || haystack.includes(query);
    return matchesType && matchesSearch;
  });
}

export default function FormFieldsSection({
  studio,
  workspaceId,
}) {
  const [viewMode, setViewMode] = useState('cards');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [drawerIndex, setDrawerIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteCascadeChildren, setDeleteCascadeChildren] = useState([]);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [hotCriteriaOpen, setHotCriteriaOpen] = useState(false);

  const allFields = studio.form?.leads_form_items || [];
  const displayFields = useMemo(
    () => filterFields(allFields, search, typeFilter),
    [allFields, search, typeFilter]
  );
  const canReorder = !String(search || '').trim() && !typeFilter;

  const handleReorder = useCallback(
    (fromIndex, toIndex) => {
      studio.reorderFields(fromIndex, toIndex);
    },
    [studio]
  );

  const dragReorder = useFieldDragReorder(handleReorder);

  const openDrawer = (index) => setDrawerIndex(index);
  const closeDrawer = () => setDrawerIndex(null);

  const resolveIndex = (field) =>
    allFields.findIndex(
      (item) => item.item_id === field?.item_id && item.key_name === field?.key_name
    );

  const getChildFields = (parentField) =>
    getLinkedSubStatusEntries(allFields, parentField).map(({ field }) => field);

  const handleSaveField = (index, patch) => {
    if (isStatusField(patch)) {
      const previous = allFields[index];
      const linked = updateStatusFieldAndLinks(allFields, index, patch);
      studio.setFields(
        applyParentOptionChangesToFields(linked, patch, previous?.key_options)
      );
      return;
    }
    studio.updateField(index, patch);
  };

  const handleDelete = (field) => {
    const index = resolveIndex(field);
    if (index < 0) return;

    if (isStatusField(field)) {
      const children = getChildFields(field);
      if (children.length) {
        setDeleteCascadeChildren(children);
      }
    }

    setDeleteIndex(index);
  };

  const confirmDelete = async () => {
    if (deleteIndex == null) return;

    const indicesToRemove = [deleteIndex];
    deleteCascadeChildren.forEach((child) => {
      const childIndex = resolveIndex(child);
      if (childIndex >= 0) {
        indicesToRemove.push(childIndex);
      }
    });

    const sortedIndices = [...new Set(indicesToRemove)].sort((a, b) => b - a);
    for (const index of sortedIndices) {
      await studio.deletePersistedField(index);
    }

    setDeleteIndex(null);
    setDeleteCascadeChildren([]);
  };

  const deleteMessage =
    deleteCascadeChildren.length > 0
      ? `This status field has ${deleteCascadeChildren.length} linked sub-status field(s). Deleting it will remove those fields as well.`
      : 'This will remove the field from the form. Saved fields are deleted immediately on the server.';

  return (
    <FormSection
      title="Form fields"
      description="Add, reorder and configure fields for this form."
      >
      <FormFieldsToolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        fieldCount={displayFields.length}
        onAddField={() => {
          studio.addField();
          setDrawerIndex(allFields.length);
        }}
      />
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap:{xs:spacing.size1 , sm:spacing.size1} , flexFlow:{xs:'column',sm:'unset'}}}>
        <Button variant="outline" onClick={() => setGroupModalOpen(true)}>
          Manage groups
        </Button>
        <Button variant="outline" onClick={() => setHotCriteriaOpen(true)} sx={{ marginLeft:'0px !important' }}>
          Hot record criteria
        </Button>
      </Stack>

      {!canReorder ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Clear search and type filters to drag-and-drop reorder fields.
        </Typography>
      ) : null}

      {viewMode === 'cards' ? (
        <Stack spacing={1.5}>
          {displayFields.map((field) => {
            const index = resolveIndex(field);
            return (
              <FormFieldCard
                key={`${field.item_id}-${field.key_name}`}
                field={field}
                index={index}
                fieldErrors={studio.fieldErrors?.[index]}
                draggedIndex={dragReorder.draggedIndex}
                dragEnabled={canReorder}
                onDragStart={dragReorder.handleDragStart}
                onDragOver={dragReorder.handleDragOver}
                onDrop={dragReorder.handleDrop}
                onDragEnd={dragReorder.handleDragEnd}
                onEdit={() => openDrawer(index)}
                onDuplicate={() => studio.duplicateField(index)}
                onDelete={() => handleDelete(field)}
              />
            );
          })}
        </Stack>
      ) : (
        <FormFieldsTableView
          fields={displayFields}
          allFields={allFields}
          fieldErrors={studio.fieldErrors}
          enableReorder={canReorder}
          onReorder={(reorderedFields) => {
            const remaining = allFields.filter(
              (field) => !displayFields.some(
                (shown) => shown.item_id === field.item_id && shown.key_name === field.key_name
              )
            );
            studio.setFields([...reorderedFields, ...remaining]);
          }}
          onEdit={(field) => openDrawer(resolveIndex(field))}
          onDelete={handleDelete}
        />
      )}

      <FormFieldDrawer
        open={drawerIndex != null}
        fieldIndex={drawerIndex}
        field={drawerIndex != null ? allFields[drawerIndex] : null}
        allFields={allFields}
        fieldErrors={drawerIndex != null ? studio.fieldErrors?.[drawerIndex] : null}
        workspaceId={workspaceId}
        groupOptions={studio.form?.group_options || []}
        onUpdateGroups={studio.updateGroups}
        onManageGroups={() => setGroupModalOpen(true)}
        onClose={closeDrawer}
        onSave={handleSaveField}
      />

      <FormConfirmDialog
        open={deleteIndex != null}
        onCancel={() => {
          setDeleteIndex(null);
          setDeleteCascadeChildren([]);
        }}
        onConfirm={confirmDelete}
        title="Delete field?"
        message={deleteMessage}
        confirmLabel="Delete field"
        cancelLabel="Keep field"
      />

      <GroupModal
        showOptionModal={groupModalOpen}
        onCancel={() => setGroupModalOpen(false)}
        group={(studio.form?.group_options || []).map((group) => group.title)}
        columnInfo={{}}
        fields={allFields}
        onSubmit={(updatedGroupTitles, info) => {
          const removedGroupTitles = (info?.removedGroups || []).map((title) => String(title || '').trim());
          const existingGroupOptions = studio.form?.group_options || [];
          const updatedGroupOptions = existingGroupOptions.filter((group) => {
            const title = String(group?.title || '').trim();
            return title && !removedGroupTitles.includes(title);
          });

          const groupFieldMap = info?.groupFieldMap || {};
          const titleSet = new Set(updatedGroupTitles.map((value) => String(value || '').trim()));
          updatedGroupTitles.forEach((title) => {
            const cleanedTitle = String(title || '').trim();
            if (!cleanedTitle) return;

            const existingGroup = existingGroupOptions.find(
              (group) => String(group?.title || '').trim() === cleanedTitle
            );

            const fieldKeys = Array.isArray(groupFieldMap[cleanedTitle])
              ? groupFieldMap[cleanedTitle]
              : existingGroup?.field_keys || [];

            if (!updatedGroupOptions.some((group) => String(group.title || '').trim() === cleanedTitle)) {
              updatedGroupOptions.push({
                id: existingGroup?.id ?? updatedGroupOptions.length + 1,
                title: cleanedTitle,
                field_keys: fieldKeys,
              });
            } else {
              const target = updatedGroupOptions.find(
                (group) => String(group.title || '').trim() === cleanedTitle
              );
              if (target) target.field_keys = fieldKeys;
            }
          });

          studio.updateGroups(updatedGroupOptions);
          const selectedFieldKeys = new Set(
            Object.entries(groupFieldMap).flatMap(([groupTitle, values]) =>
              titleSet.has(groupTitle) ? values : []
            )
          );

          studio.setFields(
            allFields.map((item) => {
              const fieldKey = item?.item_id ?? item?.key_name ?? '';
              const matchingGroupTitle = Object.entries(groupFieldMap).find(([, keys]) =>
                Array.isArray(keys) && keys.includes(fieldKey)
              )?.[0];

              const isInSelectedFieldSet = selectedFieldKeys.has(fieldKey);
              if (!matchingGroupTitle && !isInSelectedFieldSet) {
                return { ...item, groupId: '', group_title: '', isGroup: false };
              }

              if (matchingGroupTitle) {
                return {
                  ...item,
                  groupId: String(updatedGroupOptions.find((group) => group.title === matchingGroupTitle)?.id ?? ''),
                  group_title: matchingGroupTitle,
                  isGroup: true,
                };
              }

              const title = String(item?.group_title || '').trim();
              if (!title || removedGroupTitles.includes(title) || !titleSet.has(title)) {
                return { ...item, groupId: '', group_title: '', isGroup: false };
              }
              return item;
            })
          );
          setGroupModalOpen(false);
        }}
      />

      <HotLeadCriteriaModal
        open={hotCriteriaOpen}
        onClose={() => setHotCriteriaOpen(false)}
        onSave={(criteria) => {
          studio.updateHotCriteria(criteria);
          setHotCriteriaOpen(false);
        }}
        hotLeadFields={allFields.filter((field) => field.is_hot_lead)}
        dropdownOptions={allFields
          .filter((field) => ['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(field.key_type))
          .map((field) => ({
            key_name: field.key_name,
            key_options: field.key_options || [],
          }))}
      />
    </FormSection>
  );
}
